/* eslint-disable max-lines-per-function */
import { forwardRef, useEffect, useRef, useState } from 'react';

import { useLocation } from 'react-router';

import { useChatSidebar } from '@/contexts/useChatSidebar';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationRole } from '@/hooks/useOrganizationRole';
import { useSubscription } from '@/hooks/useSubscription';
import { useIsDesktop } from '@/hooks/useViewportBreakpoints';
import { cn } from '@/lib/utils';

import { StellaAvatar } from '@/components/stella/StellaAvatar';

import { isHiddenChatRoute } from './utils';

interface TriggerButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isAnimatingOpen: boolean;
}

const TriggerButton = forwardRef<HTMLButtonElement, TriggerButtonProps>(
  ({ isAnimatingOpen, onClick, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cn(
        'fixed bottom-4 right-4 lg:bottom-14 lg:right-14 z-40',
        'h-12 w-12',
        'rounded-full',
        'bg-brand text-on-brand',
        'shadow-lg hover:shadow-xl',
        'transition-all duration-200',
        'animate-in fade-in slide-in-from-bottom-2',
        'animation-duration-[180ms] animation-ease-out',
        'hover:scale-110',
        isAnimatingOpen && 'scale-92 animate-scale-bounce',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background'
      )}
      aria-label="Open Stella AI Assistant"
      {...props}
    >
      <StellaAvatar size="lg" />
    </button>
  )
);
TriggerButton.displayName = 'TriggerButton';

export const ChatSidebarTrigger = () => {
  const { user } = useAuth();
  const { subscription, isLoading } = useSubscription();
  const { isOrgLoaded, isOrgActive } = useOrganizationRole();
  const { isOpen, openChat, closeChat } = useChatSidebar();
  const isDesktop = useIsDesktop();
  const { pathname } = useLocation();
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const isHiddenRoute =
    isHiddenChatRoute(pathname) || pathname.startsWith('/onboarding');

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const activeSubscription =
    !isLoading &&
    (subscription?.status === 'active' || subscription?.status === 'trialing');
  const activeOrganization = isOrgLoaded && isOrgActive;

  useEffect(() => {
    if (!activeSubscription && !activeOrganization) {
      closeChat();
    }
  }, [activeSubscription, activeOrganization, closeChat]);

  if (
    !user ||
    isOpen ||
    isHiddenRoute ||
    (!activeSubscription && !activeOrganization)
  ) {
    return <></>;
  }

  const handleClick = () => {
    setIsAnimatingOpen(true);
    openChat({ mode: isDesktop ? 'sidebar' : 'floating' });
    timeoutRef.current = window.setTimeout(
      () => setIsAnimatingOpen(false),
      300
    );
  };

  return (
    <TriggerButton
      onClick={handleClick}
      isAnimatingOpen={isAnimatingOpen}
      title="Ask Stella"
    />
  );
};
